import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Eye,
  EyeOff,
  Filter,
  Info,
  Loader2,
  Lock,
  Server,
  Shield,
  ShieldCheck,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useToast } from "@/components/shared";
import { governanceService } from "@/lib/services";
import type { GovernanceDeployment, GovernanceOverview, RaiPolicy } from "@/types";

/** Severity threshold badge colours */
const SEVERITY_COLORS: Record<string, string> = {
  Low: "bg-green-500/20 text-green-400",
  Medium: "bg-yellow-500/20 text-yellow-400",
  High: "bg-red-500/20 text-red-400",
};

/** Human-readable filter category descriptions */
const FILTER_DESCRIPTIONS: Record<string, string> = {
  Hate: "Hate speech and discriminatory language targeting identity groups",
  Sexual: "Sexual content, nudity, and related material",
  Violence: "Violent content, weapons, and physical harm",
  Selfharm: "Self-harm, suicide, and eating disorder content",
  SelfHarm: "Self-harm, suicide, and eating disorder content",
  Jailbreak: "Prompt injection and jailbreak attempts",
  "Protected Material Text": "Known copyrighted text (lyrics, articles, etc.)",
  "Protected Material Code": "Code matching public repositories",
  Profanity: "Profane language",
};

function SeverityBadge({ threshold }: Readonly<{ threshold: string }>) {
  if (!threshold) return <span className="text-content-quaternary text-xs">N/A</span>;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        SEVERITY_COLORS[threshold] || "bg-surface-tertiary text-content-tertiary"
      }`}
    >
      {threshold}
    </span>
  );
}

function FilterStatusBadge({ enabled, blocking }: Readonly<{ enabled: boolean; blocking: boolean }>) {
  if (!enabled) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-tertiary text-content-quaternary">
        <EyeOff className="h-3 w-3" />
        Disabled
      </span>
    );
  }
  if (blocking) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
        <Lock className="h-3 w-3" />
        Blocking
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
      <Eye className="h-3 w-3" />
      Annotate Only
    </span>
  );
}

function PolicyCard({ policy }: Readonly<{ policy: RaiPolicy }>) {
  const [expanded, setExpanded] = useState(false);

  const promptFilters = policy.contentFilters.filter((f) => f.source === "Prompt");
  const completionFilters = policy.contentFilters.filter((f) => f.source === "Completion");

  const activeFilterCount = policy.contentFilters.filter((f) => f.enabled).length;
  const blockingFilterCount = policy.contentFilters.filter((f) => f.enabled && f.blocking).length;

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-surface-secondary/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-primary/20 flex items-center justify-center">
            <Filter className="h-5 w-5 text-brand-primary" />
          </div>
          <div>
            <h4 className="font-medium text-content-primary">{policy.name}</h4>
            <p className="text-xs text-content-tertiary mt-0.5">
              {activeFilterCount} active filter{activeFilterCount === 1 ? "" : "s"} &middot; {blockingFilterCount}{" "}
              blocking
              {policy.customBlocklists.length > 0 && (
                <>
                  {" "}
                  &middot; {policy.customBlocklists.length} blocklist{policy.customBlocklists.length === 1 ? "" : "s"}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {policy.basePolicyName && (
            <span className="text-xs text-content-quaternary">Base: {policy.basePolicyName}</span>
          )}
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-content-tertiary" />
          ) : (
            <ChevronRight className="h-4 w-4 text-content-tertiary" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border-primary px-4 pb-4 space-y-4">
          {/* Prompt Filters */}
          {promptFilters.length > 0 && (
            <div className="mt-4">
              <h5 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-2">
                Input (Prompt) Filters
              </h5>
              <div className="space-y-2">
                {promptFilters.map((filter) => (
                  <div
                    key={`prompt-${filter.name}`}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-secondary"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-content-primary">{filter.name}</span>
                        <FilterStatusBadge enabled={filter.enabled} blocking={filter.blocking} />
                      </div>
                      {FILTER_DESCRIPTIONS[filter.name] && (
                        <p className="text-xs text-content-quaternary mt-0.5">{FILTER_DESCRIPTIONS[filter.name]}</p>
                      )}
                    </div>
                    <div className="ml-4 shrink-0">
                      <SeverityBadge threshold={filter.severityThreshold} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completion Filters */}
          {completionFilters.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-2">
                Output (Completion) Filters
              </h5>
              <div className="space-y-2">
                {completionFilters.map((filter) => (
                  <div
                    key={`completion-${filter.name}`}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-secondary"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-content-primary">{filter.name}</span>
                        <FilterStatusBadge enabled={filter.enabled} blocking={filter.blocking} />
                      </div>
                      {FILTER_DESCRIPTIONS[filter.name] && (
                        <p className="text-xs text-content-quaternary mt-0.5">{FILTER_DESCRIPTIONS[filter.name]}</p>
                      )}
                    </div>
                    <div className="ml-4 shrink-0">
                      <SeverityBadge threshold={filter.severityThreshold} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attached Blocklists */}
          {policy.customBlocklists.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider mb-2">
                Attached Blocklists
              </h5>
              <div className="space-y-2">
                {policy.customBlocklists.map((bl) => (
                  <div
                    key={bl.blocklistName}
                    className="flex items-center justify-between py-2 px-3 rounded-lg bg-surface-secondary"
                  >
                    <span className="text-sm text-content-primary">{bl.blocklistName}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-content-quaternary">{bl.source}</span>
                      <FilterStatusBadge enabled blocking={bl.blocking} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DeploymentTable({
  deployments,
  policies,
}: Readonly<{ deployments: GovernanceDeployment[]; policies: RaiPolicy[] }>) {
  const policyNames = new Set(policies.map((p) => p.name));

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-primary">
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">Deployment</th>
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">Model</th>
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">Format</th>
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">Provider</th>
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">SKU</th>
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">Capacity</th>
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">Content Filter Policy</th>
              <th className="text-left px-4 py-3 font-medium text-content-tertiary">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary">
            {deployments.map((dep) => (
              <tr key={`${dep.provider}-${dep.name}`} className="hover:bg-surface-secondary/50 transition-colors">
                <td className="px-4 py-3 font-medium text-content-primary">{dep.name}</td>
                <td className="px-4 py-3 text-content-secondary">{dep.modelName}</td>
                <td className="px-4 py-3 text-content-tertiary">{dep.modelFormat || "\u2014"}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      dep.provider === "AI Foundry"
                        ? "bg-purple-500/20 text-purple-400"
                        : "bg-blue-500/20 text-blue-400"
                    }`}
                  >
                    {dep.provider || "Azure OpenAI"}
                  </span>
                </td>
                <td className="px-4 py-3 text-content-tertiary">{dep.skuName}</td>
                <td className="px-4 py-3 text-content-tertiary">{dep.skuCapacity || "\u2014"}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${(() => {
                      if (dep.raiPolicyName?.startsWith("Microsoft.Default")) return "bg-blue-500/20 text-blue-400";
                      if (policyNames.has(dep.raiPolicyName)) return "bg-purple-500/20 text-purple-400";
                      return "bg-surface-tertiary text-content-tertiary";
                    })()}`}
                  >
                    <ShieldCheck className="h-3 w-3" />
                    {dep.raiPolicyName || "None"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      dep.provisioningState === "Succeeded"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-yellow-500/20 text-yellow-400"
                    }`}
                  >
                    {dep.provisioningState}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminGovernance() {
  const { showToast } = useToast();
  const [overview, setOverview] = useState<GovernanceOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGovernance();
  }, []);

  const loadGovernance = async () => {
    try {
      setLoading(true);
      const response = await governanceService.getOverview();
      setOverview(response.data);
    } catch (err) {
      console.error("Failed to load governance data:", err);
      showToast("Failed to load governance data", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (!overview) return null;

  // Not configured state
  if (!overview.configured) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <div className="h-16 w-16 rounded-2xl bg-yellow-500/20 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-yellow-400" />
          </div>
          <h3 className="text-lg font-semibold text-content-primary">Governance Not Configured</h3>
          <p className="mt-2 text-content-tertiary text-sm">
            {overview.message ||
              "Set AZURE_SUBSCRIPTION_ID, AZURE_OPENAI_RESOURCE_GROUP, and AZURE_OPENAI_ACCOUNT_NAME to enable the governance dashboard."}
          </p>
        </div>
      </div>
    );
  }

  // Summary stats
  const totalFilters = overview.policies.reduce((sum, p) => sum + p.contentFilters.filter((f) => f.enabled).length, 0);
  const totalBlocking = overview.policies.reduce(
    (sum, p) => sum + p.contentFilters.filter((f) => f.enabled && f.blocking).length,
    0,
  );
  const totalDeployments = overview.deployments.length;
  const totalBlocklists = overview.blocklists.length;

  return (
    <div className="space-y-6">
      {/* Account info bar */}
      {overview.accounts.length > 0 && (
        <div className="flex items-center gap-3 text-xs text-content-quaternary flex-wrap">
          <Server className="h-3.5 w-3.5" />
          {overview.accounts.map((acc, idx) => (
            <React.Fragment key={acc.name}>
              {idx > 0 && <span className="text-border-primary">|</span>}
              <span>
                <span className="text-content-tertiary font-medium">{acc.label || acc.name}</span> ({acc.name})
              </span>
            </React.Fragment>
          ))}
          <span className="text-border-primary">|</span>
          <span>
            Resource Group:{" "}
            <span className="text-content-tertiary font-medium">{overview.accounts[0]?.resourceGroup}</span>
          </span>
          <div className="flex-1" />
          <a
            href={`https://ai.azure.com`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-brand-primary hover:underline"
          >
            Open Azure AI Foundry
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <p className="text-sm text-content-tertiary">Content Filter Policies</p>
          <div className="flex items-end justify-between mt-1">
            <p className="text-2xl font-semibold text-content-primary">{overview.policies.length}</p>
          </div>
        </div>

        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <p className="text-sm text-content-tertiary">Active Filters</p>
          <div className="flex items-end justify-between mt-1">
            <p className="text-2xl font-semibold text-content-primary">{totalFilters}</p>
            <span className="text-xs text-content-quaternary">{totalBlocking} blocking</span>
          </div>
        </div>

        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <p className="text-sm text-content-tertiary">Model Deployments</p>
          <div className="flex items-end justify-between mt-1">
            <p className="text-2xl font-semibold text-content-primary">{totalDeployments}</p>
          </div>
        </div>

        <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
          <p className="text-sm text-content-tertiary">Custom Blocklists</p>
          <div className="flex items-end justify-between mt-1">
            <p className="text-2xl font-semibold text-content-primary">{totalBlocklists}</p>
          </div>
        </div>
      </div>

      {/* Platform safety info banner */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-400 mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="text-blue-300 font-medium">Azure Platform Safety</p>
          <p className="text-blue-400/80 mt-1">
            In addition to the content filters below, Microsoft&apos;s abuse monitoring system runs on all Azure AI
            usage (both Azure OpenAI and AI Foundry models). This includes automated content classification, abuse
            pattern detection, and review processes. This is always active and managed by Microsoft — it cannot be
            disabled.
          </p>
        </div>
      </div>

      {/* Content Filter Policies */}
      <div>
        <h3 className="font-medium text-content-primary mb-3 flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Content Filter Policies
        </h3>
        {overview.policies.length === 0 ? (
          <div className="bg-surface-primary border border-border-primary rounded-xl p-8 text-center">
            <p className="text-content-tertiary">
              No custom content filter policies found. All deployments are using the Azure default policy.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {overview.policies.map((policy) => (
              <PolicyCard key={policy.name} policy={policy} />
            ))}
          </div>
        )}
      </div>

      {/* Deployment Configuration */}
      <div>
        <h3 className="font-medium text-content-primary mb-3 flex items-center gap-2">
          <Server className="h-4 w-4" />
          Deployment Safety Configuration
        </h3>
        {overview.deployments.length === 0 ? (
          <div className="bg-surface-primary border border-border-primary rounded-xl p-8 text-center">
            <p className="text-content-tertiary">No deployments found.</p>
          </div>
        ) : (
          <DeploymentTable deployments={overview.deployments} policies={overview.policies} />
        )}
      </div>

      {/* Blocklists */}
      <div>
        <h3 className="font-medium text-content-primary mb-3 flex items-center gap-2">
          <Lock className="h-4 w-4" />
          Custom Blocklists
        </h3>
        {overview.blocklists.length === 0 ? (
          <div className="bg-surface-primary border border-border-primary rounded-xl p-8 text-center">
            <p className="text-content-tertiary">
              No custom blocklists configured. Blocklists allow you to block specific terms or phrases in prompts and
              completions.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {overview.blocklists.map((bl) => (
              <div key={bl.name} className="bg-surface-primary border border-border-primary rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4 text-content-tertiary" />
                  <h4 className="font-medium text-content-primary">{bl.name}</h4>
                </div>
                {bl.description && <p className="text-xs text-content-tertiary mb-2">{bl.description}</p>}
                <div className="text-xs text-content-quaternary">
                  {bl.itemCount} term{bl.itemCount === 1 ? "" : "s"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help text */}
      <div className="text-xs text-content-quaternary border-t border-border-primary pt-4 space-y-1">
        <p>
          This dashboard shows the current content safety configuration across all Azure AI resources (Azure OpenAI and
          AI Foundry). To modify policies, blocklists, or deployment settings, use the{" "}
          <a
            href="https://ai.azure.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline"
          >
            Azure AI Foundry portal
          </a>{" "}
          .
        </p>
        <p>
          <strong>Severity thresholds:</strong> Low = strictest (blocks most content), Medium = moderate, High = most
          permissive (only blocks severe content).
        </p>
      </div>
    </div>
  );
}
